import axios from 'axios';
import { toast } from 'sonner';

import { TagSelector } from './TagSelector';
import { type DocumentKind, useDocumentTags, useSetDocumentTags } from '../api/tags-api';

interface DocumentTagPickerProps {
  documentId: string;
  kind: DocumentKind;
  /** Disable interactions when the user lacks update_document. */
  readOnly?: boolean;
}

/**
 * Per-document tag picker. Wraps TagSelector and binds it to the
 * `document-tags/{kind}/{id}` endpoint via useDocumentTags +
 * useSetDocumentTags. Mount this on document detail panes.
 */
export function DocumentTagPicker({ kind, documentId, readOnly = false }: DocumentTagPickerProps) {
  const { isLoading, data: tags = [] } = useDocumentTags(kind, documentId);
  const setTags = useSetDocumentTags(kind, documentId);

  const value = tags.map((t) => t.id);

  const handleChange = (nextIds: string[]) => {
    if (readOnly) return;
    setTags.mutateAsync(nextIds).catch((err) => {
      toast.error(extractApiError(err, 'Failed to update document tags'));
    });
  };

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">Loading tags…</div>;
  }

  return (
    <TagSelector
      value={value}
      allowCreate={!readOnly}
      placeholder={readOnly ? 'No tags' : 'No tags — click Add to apply one'}
      onChange={handleChange}
    />
  );
}

function extractApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string' && detail.length > 0) return detail;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
